import React, { useMemo, useState } from "react";
import { Search, Star, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import AILabShell from "@/components/ai-lab/AILabShell";
import ToolCard from "@/components/ai-lab/ToolCard";
import { AI_TOOLS, TOOL_GROUPS } from "@/data/ai-lab/aiTools";

/**
 * AIToolsDirectory — route: /ai-cpg-tools/tools
 * Every recommended tool grouped by job, with ONE "Recommended" badge per group.
 */
export default function AIToolsDirectory() {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");
  const [onlyRecommended, setOnlyRecommended] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return AI_TOOLS.filter((t) => {
      if (group !== "all" && t.group !== group) return false;
      if (onlyRecommended && !t.recommended) return false;
      if (!q) return true;
      return [t.name, t.tagline, t.why, ...(t.bestFor || [])].join(" ").toLowerCase().includes(q);
    });
  }, [query, group, onlyRecommended]);

  const groupsToShow = TOOL_GROUPS.filter((g) => filtered.some((t) => t.group === g.id));

  return (
    <AILabShell
      crumbs={[{ label: "AI Tools" }]}
      title="AI Tools Directory"
      subtitle="For each job we tell you which tool to use, why, and what to type into it. Start with the Recommended pick — the others are for specific situations."
    >
      {/* Filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tools… e.g. logo, voiceover, Amazon images" className="h-11 pl-9 pr-9" />
          {query && <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear"><X className="h-4 w-4" /></button>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip active={group === "all"} onClick={() => setGroup("all")}>All</Chip>
          {TOOL_GROUPS.map((g) => <Chip key={g.id} active={group === g.id} onClick={() => setGroup(g.id)}>{g.name}</Chip>)}
          <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
          <Chip active={onlyRecommended} onClick={() => setOnlyRecommended((v) => !v)} className="gap-1"><Star className="h-3 w-3" /> Recommended only</Chip>
        </div>
      </div>

      {groupsToShow.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No tools match. Try another word or clear the filters.</p>
      )}

      {groupsToShow.map((g) => (
        <section key={g.id} id={g.id} className="mb-10 scroll-mt-20">
          <div className="mb-3">
            <h2 className="text-xl font-bold tracking-tight">{g.name}</h2>
            <p className="text-sm text-muted-foreground">{g.blurb}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered
              .filter((t) => t.group === g.id)
              .sort((a, b) => Number(b.recommended) - Number(a.recommended))
              .map((tool) => <ToolCard key={tool.id} tool={tool} context="directory" />)}
          </div>
        </section>
      ))}

      <p className="text-xs text-muted-foreground">
        <Badge variant="outline" className="mr-2 font-normal">Note</Badge>
        Tools, plans and prices change often. We review this list regularly; if something looks out of date, let us know.
      </p>
    </AILabShell>
  );
}

function Chip({ active, className, children, ...rest }) {
  return (
    <Button type="button" size="sm" variant={active ? "default" : "outline"} className={cn("h-8 rounded-full", className)} {...rest}>
      {children}
    </Button>
  );
}
